import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import PrayerCard from "@/components/PrayerCard";
import PrayerFormDialog from "@/components/PrayerFormDialog";
import CategoryFilter from "@/components/CategoryFilter";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Loader2, BookOpen, Sun } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [prayers, setPrayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/", { replace: true });
  }, [authLoading, user, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { answered: false };
      if (q.trim()) params.q = q.trim();
      if (activeCat) params.category = activeCat;
      const [prayersRes, catsRes] = await Promise.all([
        api.get("/prayers", { params }),
        api.get("/categories"),
      ]);
      setPrayers(prayersRes.data);
      setCategories(catsRes.data.categories);
    } catch (e) {
      // 401 will be caught by AuthContext on next check
    } finally {
      setLoading(false);
    }
  }, [q, activeCat]);

  useEffect(() => {
    if (user) fetchAll();
  }, [user, fetchAll]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      if (user) fetchAll();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const handleCreate = async (data) => {
    const { data: created } = await api.post("/prayers", data);
    setPrayers((p) => [created, ...p]);
    toast.success("Prayer added");
    fetchAll();
  };

  const handleUpdate = async (data) => {
    const { data: updated } = await api.put(`/prayers/${editing.id}`, data);
    setPrayers((p) => p.map((x) => (x.id === updated.id ? updated : x)));
    toast.success("Prayer updated");
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/prayers/${pendingDelete.id}`);
      setPrayers((p) => p.filter((x) => x.id !== pendingDelete.id));
      toast.success("Prayer removed");
      setPendingDelete(null);
    } catch (e) {
      toast.error("Couldn't delete — please try again");
    } finally {
      setDeleting(false);
    }
  };

  const handlePrayed = async (id) => {
    const { data } = await api.post(`/prayers/${id}/prayed`);
    setPrayers((p) => p.map((x) => (x.id === id ? data : x)));
    toast.success("Marked as prayed");
  };

  const handleAnswered = async (id) => {
    await api.post(`/prayers/${id}/answered`);
    setPrayers((p) => p.filter((x) => x.id !== id));
    toast.success("Prayer moved to Answered");
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setDialogOpen(true);
  };

  const visibleCats = useMemo(() => categories.slice(0, 10), [categories]);

  // Time-of-day greeting
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  // Rotating daily scripture — one per day of the week
  const dailyScripture = useMemo(() => {
    const verses = [
      { text: "Be joyful in hope, patient in affliction, faithful in prayer.", ref: "Romans 12:12" },
      { text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God.", ref: "Philippians 4:6" },
      { text: "Rejoice always, pray continually, give thanks in all circumstances.", ref: "1 Thessalonians 5:16-18" },
      { text: "Ask and it will be given to you; seek and you will find; knock and the door will be opened.", ref: "Matthew 7:7" },
      { text: "The prayer of a righteous person is powerful and effective.", ref: "James 5:16" },
      { text: "Then you will call on me and come and pray to me, and I will listen to you.", ref: "Jeremiah 29:12" },
      { text: "Cast your cares on the LORD and he will sustain you.", ref: "Psalm 55:22" },
    ];
    return verses[new Date().getDay()];
  }, []);

  // Today's counters — computed from prayed_dates on the client
  const { prayedToday, prayedThisWeek } = useMemo(() => {
    const now = new Date();
    const today = now.toDateString();
    const weekAgo = now.getTime() - 7 * 24 * 60 * 60 * 1000;
    let today_c = 0;
    let week_c = 0;
    for (const p of prayers) {
      for (const d of p.prayed_dates || []) {
        const dt = new Date(d);
        if (dt.toDateString() === today) today_c++;
        if (dt.getTime() >= weekAgo) week_c++;
      }
    }
    return { prayedToday: today_c, prayedThisWeek: week_c };
  }, [prayers]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[#9B8BAF]" />
      </div>
    );
  }

  return (
    <div className="grain-bg min-h-screen">
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">Today</div>
            <h1 className="mt-2 text-4xl sm:text-5xl text-[#2C2A2E]" data-testid="dashboard-title">
              {greeting}, {user.name?.split(" ")[0]}
            </h1>
            <p className="mt-3 text-[#75737A] max-w-md">
              A quiet moment to hold what's on your heart before God.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="btn-primary rounded-full h-11 px-6 self-start md:self-auto"
            data-testid="new-prayer-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add a prayer
          </Button>
        </div>

        {/* Today's Scripture */}
        <section className="mt-10 rounded-2xl border border-[#D4C7E1] bg-white/70 backdrop-blur px-6 py-6 md:px-8 md:py-7" data-testid="daily-scripture">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#75737A]">
            <Sun className="h-3.5 w-3.5" /> Today's Scripture
          </div>
          <p
            className="mt-3 text-xl md:text-2xl italic text-[#2C2A2E] leading-relaxed"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;{dailyScripture.text}&rdquo;
          </p>
          <p className="mt-2 text-xs text-[#9B8BAF] uppercase tracking-[0.18em]">— {dailyScripture.ref}</p>
        </section>

        {/* Today's Prayer List header */}
        <div className="mt-12 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">Today's Prayer List</div>
            <h2 className="mt-2 text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {prayers.length === 0 ? "Nothing here yet" : `${prayers.length} on your heart`}
            </h2>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="mt-6 space-y-5">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#75737A]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your prayers…"
              className="pl-11 h-11 bg-white border-[#D4C7E1] rounded-full"
              data-testid="search-input"
            />
          </div>
          <CategoryFilter categories={visibleCats} active={activeCat} onChange={setActiveCat} />
        </div>

        {/* Prayer grid */}
        <section className="mt-10">
          {loading ? (
            <div className="py-24 flex items-center justify-center text-[#75737A]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : prayers.length === 0 ? (
            <EmptyState onCreate={openCreate} hasFilter={Boolean(q || activeCat)} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
              {prayers.map((p, i) => (
                <PrayerCard
                  key={p.id}
                  prayer={p}
                  index={i}
                  onEdit={() => openEdit(p)}
                  onDelete={() => setPendingDelete(p)}
                  onPrayed={() => handlePrayed(p.id)}
                  onAnswered={() => handleAnswered(p.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Daily stats footer */}
        {prayers.length > 0 && (
          <div
            className="mt-10 pt-6 border-t border-[#EFEBF4] flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#75737A]"
            data-testid="daily-stats"
          >
            <span>
              <b className="text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{prayedToday}</b>
              {" "}prayed today
            </span>
            <span className="text-[#D4C7E1]">·</span>
            <span>
              <b className="text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{prayedThisWeek}</b>
              {" "}this week
            </span>
          </div>
        )}
      </main>

      <PrayerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        availableCategories={categories}
        onSubmit={editing ? handleUpdate : handleCreate}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        title="Delete this prayer?"
        description={
          pendingDelete
            ? `"${pendingDelete.title}" will be permanently removed. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete prayer"
        cancelLabel="Keep it"
        destructive
        loading={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function EmptyState({ onCreate, hasFilter }) {
  return (
    <div className="py-16 md:py-24 text-center" data-testid="empty-state">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#EFEBF4] flex items-center justify-center">
        <BookOpen className="h-6 w-6 text-[#9B8BAF]" />
      </div>
      <h3 className="mt-6 text-2xl text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
        {hasFilter ? "Nothing matches yet" : "Your journal is quiet"}
      </h3>
      <p className="mt-2 text-[#75737A] max-w-sm mx-auto">
        {hasFilter
          ? "Try a different search or clear your filters."
          : "Begin with one small prayer — a name, a hope, a thank you."}
      </p>
      {!hasFilter && (
        <Button
          onClick={onCreate}
          className="btn-primary rounded-full mt-8 px-6 h-11"
          data-testid="empty-state-cta"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add your first prayer
        </Button>
      )}
    </div>
  );
}
